import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Inbox as InboxIcon,
  ListTree,
  RotateCcw,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Segmented } from '@/components/Segmented';
import { cn } from '@/lib/cn';
import { motionSafe, springDefault } from '@/lib/motion';
import { toast } from '@/lib/toast';
import { isDumpVisible } from './inboxFilter';
import { EmailSource } from './EmailSource';
import { AcceptAllBar, ReviewItem } from './ReviewItem';
import {
  useAiSettings,
  useBraindumps,
  useCreateBraindump,
  useDecideItems,
} from './useBraindump';
import type { Braindump, BraindumpSource } from './types';

const DRAFT_KEY = 'chronos-braindump-draft';

type SourceFilter = 'all' | BraindumpSource;

export function InboxPage() {
  const [text, setText] = useState(() => localStorage.getItem(DRAFT_KEY) ?? '');
  const [filter, setFilter] = useState<SourceFilter>('all');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const ai = useAiSettings();
  const dumps = useBraindumps(filter === 'all' ? undefined : filter);
  // Applied means nothing is left to decide -- every item was accepted or
  // rejected. Once that happens the dump has done its job; leaving it in the
  // queue would mean deleting it by hand just to make the list match reality.
  const visible = dumps.data?.filter(isDumpVisible);
  const create = useCreateBraindump();
  const { accept, reject, retry, discard } = useDecideItems();

  // The draft survives reload. Losing a dump you already emptied your head
  // into is the worst possible failure for this screen.
  useEffect(() => {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  }, [text]);

  const submit = () => {
    if (!text.trim()) return;
    create.mutate(text.trim(), {
      onSuccess: (dump) => {
        setText('');
        localStorage.removeItem(DRAFT_KEY);
        if (dump.status === 'failed') return;
        toast.show(`Found ${dump.items.length} ${dump.items.length === 1 ? 'task' : 'tasks'}`);
      },
    });
  };

  const configured = ai.data?.configured && ai.data.enabled;
  const showSkeleton = dumps.isPending && !dumps.data;
  const showHardError = dumps.isError && !dumps.data;

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-6">
      <header className="mb-5">
        <h1 className="text-title font-semibold">Inbox</h1>
        <p className="mt-1 text-small text-text-secondary">
          Everything captured but not yet decided, what you dumped, and what arrived by
          email. Nothing is added until you say so.
        </p>
      </header>

      {ai.data && !configured && <NotConfigured hasKey={ai.data.configured} />}

      <div className="mb-5">
        <label htmlFor="braindump-text" className="sr-only">
          Braindump
        </label>
        <textarea
          ref={textareaRef}
          id="braindump-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Enter is a newline here, this is prose, not a form field.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          rows={8}
          disabled={!configured || create.isPending}
          placeholder={
            'call the dentist, need to email dave the q3 mockups before friday, ' +
            "acme invoice is overdue, book flights for the conference, laundry…\n\n" +
            'Dictate with your keyboard mic button on a phone.'
          }
          className={cn(
            'w-full resize-y rounded-lg border border-border bg-surface-1 p-3',
            'text-body outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border disabled:opacity-50',
          )}
        />

        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          <p className="text-small text-text-tertiary">
            {ai.data && (
              <span className="tabular">
                ${Number(ai.data.month_to_date_usd).toFixed(2)} this month
              </span>
            )}
          </p>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!configured || !text.trim()}
            loading={create.isPending}
          >
            <ListTree size={15} strokeWidth={1.5} aria-hidden />
            {create.isPending ? 'Reading…' : 'Sort it out'}
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Segmented<SourceFilter>
          label="Show"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'All' },
            { value: 'email', label: 'Email' },
            { value: 'manual', label: 'Dump' },
            { value: 'shortcut', label: 'Siri' },
          ]}
        />
      </div>

      {showSkeleton && (
        <ul className="space-y-2" aria-hidden>
          {[0, 1].map((i) => (
            <li key={i} className="h-24 animate-pulse rounded-lg bg-surface-2" />
          ))}
        </ul>
      )}

      {showHardError && (
        <p role="alert" className="text-body text-danger">
          Could not load inbox.{' '}
          <button type="button" className="underline" onClick={() => dumps.refetch()}>
            Retry
          </button>
        </p>
      )}

      {visible?.length === 0 && !create.isPending && !showSkeleton && !showHardError && (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-border bg-surface-1 p-5">
          <InboxIcon size={20} strokeWidth={1.5} className="text-text-tertiary" aria-hidden />
          <p className="text-body font-medium">
            {filter === 'email'
              ? 'No email captured yet'
              : filter === 'manual'
                ? 'Nothing dumped yet'
                : 'Inbox is clear'}
          </p>
          <p className="text-small text-text-secondary">
            {filter === 'email'
              ? 'Mail sent to your capture address is checked every 5 minutes, split into tasks, and left here for you to review.'
              : 'Type everything on your mind, in any order. It gets split into tasks, routed to the right client, and estimated, then you decide what stays.'}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <AnimatePresence initial={false}>
          {visible?.map((dump) => (
            <motion.div
              key={dump.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={motionSafe(springDefault)}
            >
              <DumpCard
                dump={dump}
                onAccept={(ids) => accept.mutate({ dumpId: dump.id, itemIds: ids })}
                onReject={(ids) => reject.mutate({ dumpId: dump.id, itemIds: ids })}
                onRetry={() => retry.mutate(dump.id)}
                onDiscard={() => discard.mutate(dump.id)}
                busy={accept.isPending || reject.isPending || retry.isPending}
                aiReady={Boolean(configured)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function DumpCard({
  dump,
  onAccept,
  onReject,
  onRetry,
  onDiscard,
  busy,
  aiReady,
}: {
  dump: Braindump;
  onAccept: (ids?: number[]) => void;
  onReject: (ids?: number[]) => void;
  onRetry: () => void;
  onDiscard: () => void;
  busy: boolean;
  aiReady: boolean;
}) {
  // The failure said the key was missing; a key exists now. The message is a
  // record of that moment, so mark it superseded rather than leaving it to
  // look like the truth.
  const resolved =
    aiReady && !!dump.error && /api key/i.test(dump.error);

  const pending = dump.items.filter((i) => i.state === 'proposed');
  const confident = pending.filter(
    (i) =>
      (i.confidence ?? 1) >= dump.low_confidence_threshold &&
      (i.client_confidence ?? 1) >= dump.low_confidence_threshold,
  );

  const isEmail = dump.source === 'email';
  const subject = dump.email_subject_display || dump.email_subject;

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface-1">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-label font-medium',
                isEmail ? 'bg-surface-2 text-text-secondary' : 'bg-accent-quiet text-accent',
              )}
            >
              {isEmail ? 'Email' : 'Braindump'}
            </span>
            <time className="text-label text-text-tertiary">
              {new Date(
                (isEmail && dump.email_received_at) || dump.created_at,
              ).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </time>
          </div>
          {isEmail && subject && (
            <p className="mt-0.5 truncate text-small text-text-tertiary">{subject}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {dump.status === 'failed' && (
            <Button variant="ghost" onClick={onRetry} loading={busy} className="min-h-touch px-2">
              <RotateCcw size={14} strokeWidth={1.5} aria-hidden />
              Retry
            </Button>
          )}
          <button
            type="button"
            onClick={onDiscard}
            aria-label={isEmail ? 'Discard this email' : 'Discard this braindump'}
            className="flex size-action items-center justify-center rounded-full text-text-tertiary hover:bg-surface-2 hover:text-danger"
          >
            <Trash2 size={14} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </header>

      {dump.status === 'failed' && (
        <div className="border-b border-border px-3 py-2">
          <p className="text-small font-medium text-text-secondary">Couldn&rsquo;t sort this one</p>
          <p className="mt-0.5 text-small text-text-tertiary">{dump.error ?? 'Parsing failed.'}</p>
          {resolved && (
            <p className="mt-1 text-small text-success">
              Your key is set up now. Try again and this should go through.
            </p>
          )}
          {!isEmail && (
            <p className="mt-2 whitespace-pre-wrap text-small text-text-secondary">{dump.raw_text}</p>
          )}
        </div>
      )}

      {dump.items.length > 0 && (
        <>
          <AcceptAllBar
            pending={pending.length}
            confident={confident.length}
            onAcceptAll={() => onAccept()}
            onAcceptConfident={() => onAccept(confident.map((i) => i.id))}
            onRejectAll={() => onReject()}
            busy={busy}
          />

          <ul className="divide-y divide-border">
            {dump.items.map((item) => (
              <ReviewItem
                key={item.id}
                item={item}
                threshold={dump.low_confidence_threshold}
                onAccept={() => onAccept([item.id])}
                onReject={() => onReject([item.id])}
              />
            ))}
          </ul>
        </>
      )}

      {isEmail && <EmailSource dump={dump} />}
    </section>
  );
}

function NotConfigured({ hasKey }: { hasKey: boolean }) {
  return (
    <div className="mb-5 rounded-lg border border-border bg-surface-2 p-4">
      <p className="text-body font-medium">
        {hasKey ? 'AI parsing is switched off' : 'Add an Anthropic API key'}
      </p>
      <p className="mt-1 text-small text-text-secondary">
        {hasKey
          ? 'Turn it back on in Settings to sort dumps automatically.'
          : 'Braindump uses your own Anthropic key. Add it in Settings: it is encrypted before it is stored, and you can see what it costs on this page.'}
      </p>
      <a
        href="/?view=settings"
        className="mt-3 inline-flex min-h-touch items-center rounded bg-surface-3 px-3 text-small font-medium hover:bg-surface-1"
      >
        Open Settings
      </a>
    </div>
  );
}
